import { devices_config } from '@devices/index';
import { get } from 'svelte/store';
import { format, locale } from 'svelte-i18n';
import type { Device } from 'src/types/Device';
import { get_gen_2_component_channel_structure } from './interface_helpers';

const OPERATIONAL_RUNTIME_UNITS = [
	{ value: 365 * 24 * 3600, i18nKey: 'label_y_countdow', defaultLabel: 'y' },
	{ value: 30 * 24 * 3600, i18nKey: 'label_mo_countdow', defaultLabel: 'mo' },
	{ value: 24 * 3600, i18nKey: 'label_d_countdow', defaultLabel: 'd' },
	{ value: 3600, i18nKey: 'timer_text_hr', defaultLabel: 'hr' },
	{ value: 60, i18nKey: 'timer_text_min', defaultLabel: 'min' },
	{ value: 1, i18nKey: 'timer_text_sec', defaultLabel: 'sec' },
] as const;

/**
 * Formats a runtime duration using localized short unit labels (y/mo/d/hr/min/sec).
 * @param totalSeconds - Duration in seconds
 * @param maxParts - Optional cap on how many unit parts to include (e.g. 2 → `17d 1hr`)
 */
function formatOperationalRuntime(totalSeconds: number, maxParts?: number): string {
	const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
	const $format = get(format);
	const units = OPERATIONAL_RUNTIME_UNITS.map((unit) => ({
		value: unit.value,
		label: $format(unit.i18nKey, { default: unit.defaultLabel }),
	}));
	const secondLabel = units[units.length - 1].label;

	let remaining = s;
	const parts: string[] = [];

	for (const unit of units) {
		if (remaining >= unit.value) {
			const qty = Math.floor(remaining / unit.value);
			remaining %= unit.value;
			parts.push(`${qty}${unit.label}`);
		}

		if (maxParts != null && parts.length === maxParts) break;
	}

	return parts.length ? parts.join(' ') : `0${secondLabel}`;
}

export function hasOperationalCountersInStatus(
	device: Device,
	deviceStatuses: Record<string, unknown> | undefined,
): boolean {
	const key = get_gen_2_component_channel_structure(device);
	const block = deviceStatuses?.[key] as { counts?: unknown } | undefined;
	return block != null && block.counts != null && typeof block.counts === 'object';
}

export function hasActiveLoadRuntimeSupport(
	device: Device,
	deviceStatuses: Record<string, unknown> | undefined,
): boolean {
	const deviceConfig = devices_config[device?.type];
	if (!deviceConfig?.isPM) return false;
	const key = get_gen_2_component_channel_structure(device);
	const channelStatus = deviceStatuses?.[key] as { apower?: unknown } | undefined;
	return typeof channelStatus?.apower !== 'undefined';
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
	v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Mirrors firmware behaviour when merging a partial status delta for a single
 * component block (e.g. `switch:0`, `relays[0]`).
 */
const clearStaleTimerFields = (
	merged: Record<string, unknown>,
	incoming: Record<string, unknown>,
): void => {
	const reportsActuatorState = 'output' in incoming || 'ison' in incoming;
	if (!reportsActuatorState) return;

	const incomingHasTimer =
		'timer_started_at' in incoming || 'timer_started' in incoming || 'timer_duration' in incoming;
	if (incomingHasTimer) return;

	delete merged.timer_started_at;
	delete merged.timer_started;
	delete merged.timer_duration;
	delete merged.timer_remaining;
	if ('has_timer' in merged) merged.has_timer = false;
};

function mergeComponentBlock(
	prev: Record<string, unknown>,
	incoming: Record<string, unknown>,
): Record<string, unknown> {
	const merged = { ...prev, ...incoming };
	const pc = prev.counts;
	const ic = incoming.counts;
	if (isPlainObject(pc)) {
		if (ic === undefined || ic === null) merged.counts = pc;
		else if (isPlainObject(ic)) merged.counts = { ...pc, ...ic };
	}
	clearStaleTimerFields(merged, incoming);
	return merged;
}

export function mergePartialDeviceStatus(
	prev: Record<string, unknown> | null | undefined,
	incoming: unknown,
): Record<string, unknown> {
	const p = isPlainObject(prev) ? prev : {};
	const i = isPlainObject(incoming) ? incoming : {};
	const out: Record<string, unknown> = { ...p };
	for (const key of Object.keys(i)) {
		const pv = p[key];
		const iv = i[key];
		out[key] = isPlainObject(pv) && isPlainObject(iv) ? mergeComponentBlock(pv, iv) : iv;
	}
	return out;
}

export function formatOperationalRuntimeSeconds(totalSeconds: number): string {
	return formatOperationalRuntime(totalSeconds, 2);
}

export function formatOperationalRuntimeFull(totalSeconds: number): string {
	return formatOperationalRuntime(totalSeconds);
}

export function formatOperationalResetTimestamp(ts: number | undefined | null): string {
	const sec = ts > 1e12 ? Math.floor(ts / 1000) : Math.floor(Number(ts));
	if (!Number.isFinite(sec) || sec <= 0) return '—';
	try {
		const appLocale = get(locale) || 'en';
		return new Date(sec * 1000).toLocaleString(appLocale, {
			dateStyle: 'medium',
			timeStyle: 'short',
		});
	} catch {
		return '—';
	}
}
